import React, { memo, useContext, useEffect, useMemo, useRef } from 'react';
import { Polygon } from 'ol/geom';
import { gpsToPixel, setPolygonStyle } from 'components/generics/Map/MapCommon';
import { emptyCulture } from 'utils/culturesColors';
import { getCultureCampaignKeyFromYear } from 'utils/Datagrid/CustomParcelstDataGridUtils';
import { ExploitationContext } from 'utils/context';
import ParcelInfo from 'components/generics/Map/ParcelInfo';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import MapInteractionHook from 'components/generics/Map/MapInteraction.hook';

const MapImport = memo(
  function MapImport({
    forecastCampaign,
    parcelsList,
    name,
    parcelHover,
    setHighlightedCard,
    importedParcels,
    setParcelMapHovered,
    parcelMapHovered,
    parcelFromCardToZoom,
    parcelAssoliaToZoom,
    visibilityCard,
  }) {
    const { t } = useTranslation();
    const { culturesColors } = useContext(ExploitationContext);
    const year = forecastCampaign?.startYear;
    const mapTitleRef = useRef();
    const clickHandlerRef = useRef(null);

    const parcelData = useMemo(
      () =>
        name === 'map-import'
          ? parcelsList.map((parcel) => {
              const { uuid, ...rest } = parcel.importedParcel;
              return { ...rest, id: uuid, associatedParcelId: parcel?.matchedParcel?.id || null };
            })
          : parcelsList.map((parcel) => ({
              ...parcel,
              associatedParcelId:
                importedParcels.find(
                  (importedParcel) => importedParcel?.matchedParcel?.id === parcel?.id,
                )?.importedParcel?.uuid || null,
            })),
      [name, parcelsList, importedParcels],
    );

    const getCultureForParcel = (parcel) => {
      let culture = parcel?.parcelCultures?.[getCultureCampaignKeyFromYear(year)];
      if (!culture?.name) culture = { ...emptyCulture, year };
      return culture;
    };

    const getPolygonStyleParams = (parcel, culture, opacity) => ({
      color: culture?.name ?? culture,
      opacity,
      organicState: parcel?.organicState,
      conversionDate: parcel?.conversionDate,
    });

    const mapState = MapInteractionHook({
      mapId: name,
      parcelsData: parcelData || [],
      initialYear: year,
      minYear: year,
      maxYear: year,
      getCultureForParcel,
      getPolygonStyleParams,
      dependencies: [culturesColors],
    });

    useEffect(() => {
      if (!mapState.map || !mapState.polygonSource) return;

      const onPointerMove = (e) => {
        mapState.map.forEachFeatureAtPixel(e.pixel, (f) => {
          const parcel = parcelData?.find((p) => p.id + '' + year + '' + p.name === f.getId());
          if (parcel) setParcelMapHovered(parcel.id);
          return true;
        });
      };

      const onMouseOut = () => setParcelMapHovered('none');

      mapState.map.on('pointermove', onPointerMove);
      const el = mapState.map.getTargetElement();
      el?.addEventListener('mouseout', onMouseOut);

      return () => {
        mapState.map.un('pointermove', onPointerMove);
        el?.removeEventListener('mouseout', onMouseOut);
      };
    }, [mapState.map, mapState.polygonSource, parcelData]);

    useEffect(() => {
      if (!mapState.map) return;

      if (clickHandlerRef.current) {
        mapState.map.un('click', clickHandlerRef.current);
      }

      const newClickHandler = (e) => {
        mapState.map.forEachFeatureAtPixel(e.pixel, (feature) => {
          let parcelId = feature.get('parcelId');
          if (name === 'map-assolia') {
            const correspondingUuid =
              importedParcels.find((parcel) => parcel.matchedParcel?.id === parcelId)
                ?.importedParcel.uuid ?? null;
            parcelId = correspondingUuid;
            if (parcelId === null) {
              toast.info(t('message.noCorrespondenceFound'));
              return;
            }
          }
          const element = document.getElementById(parcelId);
          if (element && visibilityCard.get(parcelId) === true) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
            setTimeout(() => {
              setHighlightedCard(parcelId);
              setTimeout(() => setHighlightedCard(null), 1500);
            }, 500);
          } else {
            toast.info(t('message.parcelCardHiddenByFilter'));
          }
        });
      };

      mapState.map.on('click', newClickHandler);
      clickHandlerRef.current = newClickHandler;

      return () => {
        if (clickHandlerRef.current) {
          mapState.map.un('click', clickHandlerRef.current);
        }
      };
    }, [mapState.map, importedParcels, name, setHighlightedCard, visibilityCard]);

    const highlightParcels = (polygonSource) => {
      if (polygonSource) {
        polygonSource.getFeatures().forEach((feature) => {
          const selectedCultureName = parcelData.find(
            (parcel) => parcel.id + '' + year + '' + parcel.name === feature.getId(),
          );
          let parcelCultureAtYear =
            selectedCultureName?.parcelCultures?.[getCultureCampaignKeyFromYear(year)];
          if (!parcelCultureAtYear?.name) {
            parcelCultureAtYear = { ...emptyCulture, year };
          }

          if (selectedCultureName) {
            const isHighlighted =
              selectedCultureName?.id === parcelHover ||
              (parcelHover === 'none' && parcelMapHovered === 'none') ||
              selectedCultureName?.associatedParcelId === parcelMapHovered;

            setPolygonStyle({
              parcelFeature: feature,
              color: parcelCultureAtYear?.name,
              opacity: isHighlighted ? 1 : 0.3,
              culturesColors,
              organicState: selectedCultureName?.organicState,
              conversionDate: selectedCultureName?.conversionDate,
              year,
            });
          }
        });
      }
    };

    useEffect(() => {
      if (mapState.map && parcelHover) {
        highlightParcels(mapState.polygonSource);
      }
    }, [mapState.map, parcelHover, parcelMapHovered, parcelData]);

    useEffect(() => {
      if (
        !mapState.map ||
        !mapState.polygonSource ||
        (parcelFromCardToZoom.assoliaParcel === 'none' &&
          parcelFromCardToZoom.importedParcel === 'none')
      )
        return;

      const features = mapState.polygonSource.getFeatures();
      const targetFeature = features.find((feature) => {
        const id = feature.get('parcelId');
        return (
          id === parcelFromCardToZoom.assoliaParcel || id === parcelFromCardToZoom.importedParcel
        );
      });

      const extent = targetFeature
        ? targetFeature.getGeometry()?.getExtent()
        : new Polygon(gpsToPixel(parcelFromCardToZoom.coordinates)).getExtent();

      if (extent) {
        mapState.map.getView().fit(extent, {
          size: mapState.map.getSize(),
          padding: [150, 150, 150, 150],
          duration: 1000,
          maxZoom: 15,
        });
      }
    }, [parcelFromCardToZoom]);

    useEffect(() => {
      if (
        !mapState.map ||
        !mapState.polygonSource ||
        parcelAssoliaToZoom === 'none' ||
        parcelAssoliaToZoom.coordinates.length === 0
      )
        return;

      let extent = null;

      if (name === 'map-import') {
        extent = new Polygon(gpsToPixel(parcelAssoliaToZoom.coordinates)).getExtent();
      } else {
        const targetFeature = mapState.polygonSource
          .getFeatures()
          .find((f) => f.get('parcelId') === parcelAssoliaToZoom.assoliaParcel);
        extent = targetFeature?.getGeometry()?.getExtent() ?? null;
      }

      if (extent) {
        mapState.mapElement.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        mapState.map.getView().fit(extent, {
          size: mapState.map.getSize(),
          padding: [150, 150, 150, 150],
          duration: 1000,
          maxZoom: 15,
        });
      }
    }, [parcelAssoliaToZoom]);

    const mapTitleHeight = mapTitleRef?.current?.offsetHeight + 'px';

    return (
      <React.Fragment>
        <h3 ref={mapTitleRef} className="title_map">
          {t('common.map')} {t('common.parcels')}{' '}
          {name === 'map-import' ? t('label.importedPlural') : t('label.assolia')}
        </h3>
        <div className="tab_container">
          <div ref={mapState.mapElement} className="map_container">
            <div
              id={name}
              className="map_element"
              onMouseOut={() => {
                mapState.clearPopup();
                setParcelMapHovered('none');
              }}
              style={{ height: `calc(100vh - 125px - ${mapTitleHeight} - 20px - 10px)` }}
            />
            {mapState.parcelInfo && mapState.modalCoords && (
              <div className="parcel-info-modal" ref={mapState.popup}>
                <div
                  ref={mapState.popupContentRef}
                  id="popup"
                  className="ol-popup"
                  style={mapState.getPopupStyle()}
                >
                  <ParcelInfo
                    parcelInfo={mapState.parcelInfo}
                    parcels={parcelData}
                    forecastCampaign={forecastCampaign}
                    year={year}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  },
  (prevProps, nextProps) => {
    let haveCorrespondingAssoliaParcelChanged = false;
    prevProps.importedParcels.forEach((parcel) => {
      const importedParcelId = parcel.importedParcel.uuid;
      const nextPropsImportedParcel = nextProps.importedParcels.find(
        (importedParcel) => importedParcel.importedParcel.uuid === importedParcelId,
      );
      if (parcel.matchedParcel?.id !== nextPropsImportedParcel?.matchedParcel?.id) {
        haveCorrespondingAssoliaParcelChanged = true;
      }
    });
    return (
      prevProps.forecastCampaign === nextProps.forecastCampaign &&
      prevProps.parcelsList === nextProps.parcelsList &&
      prevProps.name === nextProps.name &&
      prevProps.parcelHover === nextProps.parcelHover &&
      prevProps.mapContainer === nextProps.mapContainer &&
      prevProps.setHighlightedCard === nextProps.setHighlightedCard &&
      !haveCorrespondingAssoliaParcelChanged &&
      prevProps.parcelMapHovered === nextProps.parcelMapHovered &&
      prevProps.parcelFromCardToZoom === nextProps.parcelFromCardToZoom &&
      prevProps.parcelAssoliaToZoom === nextProps.parcelAssoliaToZoom
    );
  },
);

export default MapImport;
